import React from 'react';
import { Card, CardContent, CardActions, Button, Typography, Chip, Paper, Box } from '@mui/material';

// Import tier icons
import copperIcon from '../assets/copper.png';
import nickelIcon from '../assets/nickel.png';
import bronzeIcon from '../assets/bronze.png';
import silverIcon from '../assets/silver.png';
import goldIcon from '../assets/gold.png';
import platinumIcon from '../assets/platinum.png';

function NoteCard(props) {
  const { note, handleNotePickup } = props;
  if (!note) return null;

  // Tier gradients for static/tiered notes - more metallic colors
  const tierGradients = {
    copper: 'linear-gradient(135deg, #B87333 0%, #D4915D 25%, #C9874B 50%, #B87333 75%, #D4915D 100%)',
    nickel: 'linear-gradient(135deg, #B0ACA8 0%, #C8C4C0 25%, #A8A4A0 50%, #D0CCC8 75%, #B0ACA8 100%)',
    bronze: 'linear-gradient(135deg, #CD7F32 0%, #B87333 25%, #CD7F32 50%, #E6A05C 75%, #CD7F32 100%)',
    silver: 'linear-gradient(135deg, #C0C0C0 0%, #8C92AC 25%, #E8E8E8 50%, #A8A9AD 75%, #C0C0C0 100%)',
    gold: 'linear-gradient(135deg, #FFD700 0%, #FFED4E 25%, #FFC700 50%, #FFAA00 75%, #FFD700 100%)',
    platinum: 'linear-gradient(135deg, #E5E4E2 0%, #B8B8B8 25%, #F5F5F5 50%, #D0D0D0 75%, #E5E4E2 100%)',
  };

  // Rarity colors for collectible notes (WoW-style)
  const rarityColors = {
    common: '#9d9d9d',      // Grey
    uncommon: '#1eff00',    // Green
    rare: '#0070dd',        // Blue
    epic: '#a335ee',        // Purple
    legendary: '#ff8000',   // Orange/Gold
  };

  // Determine styling based on note type
  const isCollectible = note.noteType === 'collectible';
  const hasTier = note.noteTier && tierGradients[note.noteTier];
  const hasRarity = note.rarity && rarityColors[note.rarity];

  // Get the appropriate styling
  const getCardStyle = () => {
    if (hasTier) {
      // Tiered notes get gradient shimmer background
      return {
        background: tierGradients[note.noteTier],
        backgroundSize: '200% 200%',
        animation: 'shimmer 6s ease-in-out infinite',
        border: 'none',
        color: '#000',
        minHeight: '180px',
      };
    } else if (isCollectible && hasRarity) {
      // Collectible notes get rarity-colored border
      return {
        border: `3px solid ${rarityColors[note.rarity]}`,
        backgroundColor: 'default',
        minHeight: '180px',
        boxShadow: `0 0 10px ${rarityColors[note.rarity]}40`,
      };
    } else {
      // Standard notes use default Material Paper background
      return {
        minHeight: '180px',
      };
    }
  };

  const getTierIcon = () => {
    const tierIcons = {
      copper: copperIcon,
      nickel: nickelIcon,
      bronze: bronzeIcon,
      silver: silverIcon,
      gold: goldIcon,
      platinum: platinumIcon,
    };

    if (!note.noteTier || !tierIcons[note.noteTier]) return null;

    // Don't add icon if title already contains an emoji (legacy notes)
    const hasEmoji = /[\u{1F000}-\u{1F9FF}]/u.test(note.title || '');
    if (hasEmoji) return null;

    return (
      <Box
        component="img"
        src={tierIcons[note.noteTier]}
        alt={note.noteTier}
        sx={{ width: 24, height: 24, objectFit: 'contain', mr: 0.5 }}
      />
    );
  };

  const getRarityLabel = () => {
    if (!note.rarity) return null;
    return (
      <Chip
        label={note.rarity.toUpperCase()}
        size="small"
        sx={{
          backgroundColor: rarityColors[note.rarity],
          color: '#fff',
          fontWeight: 600,
          fontSize: '0.7rem',
        }}
      />
    );
  };

  return (
    <>
      <style>
        {`
          @keyframes shimmer {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
      <Paper
        elevation={3}
        sx={{
          ...getCardStyle(),
          borderRadius: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 6,
          },
        }}
      >
        <CardContent sx={{ flexGrow: 1 }}>
          <Typography
            variant="h6"
            component="h2"
            sx={{
              fontFamily: 'Gochi Hand, cursive',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {hasTier && getTierIcon()} {note.title}
          </Typography>
          {getRarityLabel()}
          <Typography
            variant="body2"
            component="p"
            sx={{
              mt: 1,
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {note.description}
          </Typography>
          {note.pointValue > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 1,
                fontWeight: 600,
                color: hasTier ? '#000' : 'primary.main',
              }}
            >
              +{note.pointValue} points
            </Typography>
          )}
        </CardContent>
        <CardActions>
          <Button
            size="small"
            variant={note.active ? 'contained' : 'outlined'}
            onClick={() => handleNotePickup(note)}
            disabled={!note.active}
            sx={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {note.active ? 'PICK UP' : 'Picked Up'}
          </Button>
        </CardActions>
      </Paper>
    </>
  );
}

export default NoteCard;
